import i18n from '@/utils/i18n';
import InfoIcon from '@assets/icons/info.svg';
import PriceArrowDown from '@assets/icons/price-arrow-down.svg';
import PriceArrowUp from '@assets/icons/price-arrow-up.svg';
import React, { ReactElement, useMemo, useRef, useState } from 'react';
import { Analytics } from '@/utils/analytics';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import {
  DimensionValue,
  Platform,
  Pressable,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { formatCurrency, MICRO_CONVERSION } from '@/utils/money';
import { handleWebKeyPress } from '@platform/shortcuts/utils/keyboard-platform';
import { INPUT_HEIGHT, OverrideInput } from '@/components/override-input';
import { PortalHost } from '@gorhom/portal';
import { PriceEditDisplayInfoDetail, PriceEditDisplayInfoTrend } from '@/types';
import { PriceOverrideInput } from '@/components/price-override-input';
import { SalesRepInfoCell, SalesRepInfoCellText } from './sales-rep-info-cell';
import { Tooltip } from '@usepepper/ui/atoms/tooltip';
import { toPercentageString } from '@/utils/percentage';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { usePriceEditing } from '@/hooks/use-price-editing';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { v4 as uuidv4 } from 'uuid';

export enum SalesRepInfoSize {
  LARGE = 'LARGE',
  MEDIUM = 'MEDIUM',
  SMALL = 'SMALL',
}

export interface Props {
  inDraggableSheet?: boolean;
  maxCells?: number;
  onPress: () => void;
  onSalesRepPriceUpdated?: () => void;
  size?: SalesRepInfoSize;
  variantPackUUID: string;
}

const NUMBER_OF_MOBILE_CELLS_TO_DISPLAY = 2; // remaining number of cells to display on mobile, since GP% always displayed
const NUMBER_OF_HARDCODED_DESKTOP_CELLS = 2; // for price and GP%

const calculateLeftOffset = (cellIndex: number, numColumns: number) => {
  if (cellIndex === 0) {
    return 0;
  }
  // + 1 to account for view details button also being treated as a cell and + 2 to account for the hardcoded cells
  return toPercentageString(
    ((100 / (numColumns + 1)) * cellIndex) as number,
  ) as DimensionValue;
};

export const SalesRepInfo: React.FC<Props> = ({
  inDraggableSheet,
  onPress,
  onSalesRepPriceUpdated,
  size = SalesRepInfoSize.SMALL,
  maxCells,
  variantPackUUID,
}) => {
  const componentUUID = useRef(uuidv4());
  const showGrossProfit = !useSupplierDynamicConfigFlag(
    'sales_rep_config',
    'disable_gross_profit_display',
  );
  const { currencyCode, translatedTitles } = useVariantPackInfoContext();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
    user: { uuid: userUUID },
  } = useAuthenticatedUserContext();
  const [styles, theme] = useStyles();
  const [isUpdatedByActionSheetVisible, setIsUpdatedByActionSheetVisible] =
    useState(false);

  const {
    // Data
    currentPriceInMicros,
    grossProfitInMicros,
    isUpdatingGrossProfit,
    isUpdatingPrice,
    vpDisplayConfigs: rawVpDisplayConfigs,

    // Formatted display values
    priceDisplayValue,
    grossProfitDisplayValue,
    salesRepUpdatedByName,

    // Change handlers
    setSalesRepPriceInMicros,
    setSalesRepGrossProfitPercent,
    clearEdits,

    // Save callbacks
    createConfirmHandler,

    // Settings
    isPriceEditingEnabled,
    isSalesRepInfoDisabled,
  } = usePriceEditing(variantPackUUID);
  const vpDisplayConfigs = maxCells
    ? rawVpDisplayConfigs?.slice(0, maxCells)
    : rawVpDisplayConfigs;

  const handleConfirm = useMemo(
    () =>
      createConfirmHandler({
        onSuccess: () => onSalesRepPriceUpdated?.(),
      }),
    [createConfirmHandler, onSalesRepPriceUpdated],
  );

  const isInputEditable =
    isPriceEditingEnabled &&
    size === SalesRepInfoSize.LARGE &&
    !isUpdatingPrice &&
    !isUpdatingGrossProfit;

  const renderChildrenForConfig = (
    detail: PriceEditDisplayInfoDetail,
  ): ReactElement => {
    if (detail.currency !== null) {
      return (
        <View style={detail.trend !== null ? styles.salesCostContainer : null}>
          {detail.type === 'current_price' &&
          typeof currentPriceInMicros === 'number' ? (
            <SalesRepInfoCellText>
              {formatCurrency(currentPriceInMicros, currencyCode)}
            </SalesRepInfoCellText>
          ) : detail.type === 'gross_profit' &&
            typeof grossProfitInMicros === 'number' ? (
            <SalesRepInfoCellText>
              {formatCurrency(grossProfitInMicros, currencyCode)}
            </SalesRepInfoCellText>
          ) : (
            <View style={styles.salesCostContent}>
              <SalesRepInfoCellText>
                {formatCurrency(
                  Number(detail.value) * MICRO_CONVERSION,
                  currencyCode,
                )}
              </SalesRepInfoCellText>
              {detail.trend !== null ? (
                <View style={styles.salesCostIcon}>
                  {detail.trend === PriceEditDisplayInfoTrend.Up ? (
                    <PriceArrowUp />
                  ) : (
                    <PriceArrowDown />
                  )}
                </View>
              ) : null}
            </View>
          )}
        </View>
      );
    } else {
      return <SalesRepInfoCellText>{detail.value}</SalesRepInfoCellText>;
    }
  };

  const topRowContentMinHeight = isPriceEditingEnabled ? INPUT_HEIGHT : null;

  // GP% is one of the hardcoded cells; exclude it from the count when hidden
  // so that calculateLeftOffset positions the remaining cells correctly
  const numberOfHardcodedCells = showGrossProfit
    ? NUMBER_OF_HARDCODED_DESKTOP_CELLS
    : NUMBER_OF_HARDCODED_DESKTOP_CELLS - 1;

  const largeView = (
    <>
      <SalesRepInfoCell
        containerStyle={styles.divider}
        contentMinHeight={topRowContentMinHeight}
        isEditable={isPriceEditingEnabled}
        label={i18n.t('price')}
      >
        <PriceOverrideInput
          buttonContainerStyle={[
            styles.inputButtons,
            {
              left:
                size === SalesRepInfoSize.LARGE
                  ? calculateLeftOffset(
                      0,
                      vpDisplayConfigs
                        ? vpDisplayConfigs.length + numberOfHardcodedCells
                        : 0,
                    )
                  : undefined,
            },
          ]}
          currencyCode={currencyCode}
          editable={isInputEditable}
          focusable={isInputEditable}
          inDraggableSheet={inDraggableSheet}
          isConfirming={isUpdatingPrice}
          onChange={setSalesRepPriceInMicros}
          onClear={clearEdits}
          onConfirm={handleConfirm}
          onKeyPress={handleWebKeyPress}
          portalHost={`SalesRepInfo__${variantPackUUID}__${componentUUID.current}`}
          testID="price-override-input"
          value={priceDisplayValue}
        />
      </SalesRepInfoCell>
      {showGrossProfit ? (
        <SalesRepInfoCell
          containerStyle={styles.divider}
          contentMinHeight={topRowContentMinHeight}
          isEditable={isPriceEditingEnabled}
          label={i18n.t('gross_profit_percent')}
        >
          <OverrideInput
            allowNegative
            allowZero
            buttonContainerStyle={[
              styles.inputButtons,
              {
                left:
                  size === SalesRepInfoSize.LARGE
                    ? calculateLeftOffset(
                        1,
                        vpDisplayConfigs
                          ? vpDisplayConfigs.length + numberOfHardcodedCells
                          : 0,
                      )
                    : undefined,
              },
            ]}
            editable={isInputEditable}
            focusable={isInputEditable}
            inDraggableSheet={inDraggableSheet}
            isConfirming={isUpdatingGrossProfit}
            onChange={setSalesRepGrossProfitPercent}
            onClear={clearEdits}
            onConfirm={handleConfirm}
            onKeyPress={handleWebKeyPress}
            portalHost={`SalesRepInfo__${variantPackUUID}__${componentUUID.current}`}
            rightElement={
              <Text style={[TYPOGRAPHY.small, styles.inputRightElementText]}>
                {'%'}
              </Text>
            }
            testID="gross-profit-override-input"
            value={grossProfitDisplayValue}
          />
        </SalesRepInfoCell>
      ) : null}
      {vpDisplayConfigs
        ? vpDisplayConfigs.map((detail, index) => (
            <SalesRepInfoCell
              containerStyle={styles.divider}
              contentMinHeight={topRowContentMinHeight}
              key={index}
              label={translatedTitles[detail.title]}
            >
              {detail.value ? renderChildrenForConfig(detail) : '--'}
            </SalesRepInfoCell>
          ))
        : null}
    </>
  );

  // When GP% is hidden, show one extra display config cell to fill the space
  const numberOfMobileCells = showGrossProfit
    ? NUMBER_OF_MOBILE_CELLS_TO_DISPLAY
    : NUMBER_OF_MOBILE_CELLS_TO_DISPLAY + 1;

  const smallView = (
    <>
      {showGrossProfit ? (
        <SalesRepInfoCell
          containerStyle={styles.divider}
          isEditable={false}
          label={i18n.t('gross_profit_percent')}
        >
          {grossProfitDisplayValue ? (
            <SalesRepInfoCellText>
              {grossProfitDisplayValue}%
            </SalesRepInfoCellText>
          ) : (
            '--'
          )}
        </SalesRepInfoCell>
      ) : null}
      {vpDisplayConfigs
        ? vpDisplayConfigs
            .slice(0, numberOfMobileCells)
            .map((detail, index) => (
              <SalesRepInfoCell
                containerStyle={
                  index !== numberOfMobileCells - 1 ? styles.divider : null
                }
                contentMinHeight={topRowContentMinHeight}
                key={index}
                label={translatedTitles[detail.title]}
              >
                {detail.value ? renderChildrenForConfig(detail) : '--'}
              </SalesRepInfoCell>
            ))
        : null}
    </>
  );

  return (
    <>
      {!isSalesRepInfoDisabled && vpDisplayConfigs ? (
        <TouchableOpacity
          focusable={false}
          onPress={() => {
            onPress();
            Analytics.track('sales_rep_info.press', {
              restaurantUUID,
              supplierUUID,
              userUUID,
              variantPackUUID,
            });
          }}
          style={styles.container}
          testID="sales-rep-info"
        >
          <PortalHost
            name={`SalesRepInfo__${variantPackUUID}__${componentUUID.current}`}
          />
          {size === SalesRepInfoSize.LARGE ? (
            <View style={styles.contentListView}>
              {largeView}
              <View style={styles.editPriceButton}>
                <Text style={styles.editPriceTextStyle}>
                  {i18n.t('view_details')}
                </Text>
              </View>
            </View>
          ) : (
            <View style={styles.row}>{smallView}</View>
          )}
          {size === SalesRepInfoSize.SMALL ? (
            <View
              style={[styles.editPriceButton, styles.editPriceMobileButton]}
            >
              <Text style={styles.editPriceTextStyle}>
                {isPriceEditingEnabled
                  ? i18n.t('edit_price')
                  : i18n.t('view_details')}
              </Text>
            </View>
          ) : null}

          {salesRepUpdatedByName ? (
            <View style={[styles.salesRepOverrideTooltipContainer]}>
              {Platform.OS === 'android' || Platform.OS === 'ios' ? (
                <>
                  <Pressable
                    onPress={() => setIsUpdatedByActionSheetVisible(true)}
                    style={styles.salesRepOverrideTooltip}
                  >
                    <Typography
                      color={theme.sk.color.text.tertiary}
                      variant="paragraph-sm"
                    >
                      {i18n.t(
                        'sales_portal.order_entry.table.price_override_active',
                      )}
                    </Typography>
                    <InfoIcon
                      height={theme.sk.spacing.spaceSMedium}
                      stroke={theme.sk.color.icon.primary}
                      width={theme.sk.spacing.spaceSMedium}
                    />
                  </Pressable>
                  <BaseActionSheet
                    isVisible={isUpdatedByActionSheetVisible}
                    onClose={() => setIsUpdatedByActionSheetVisible(false)}
                    showCloseButton
                  >
                    <Typography
                      color={theme.sk.color.text.primary}
                      variant="paragraph-md"
                    >
                      {i18n.t('sales_portal.order_entry.table.updated_by', {
                        name: salesRepUpdatedByName,
                      })}{' '}
                    </Typography>
                  </BaseActionSheet>
                </>
              ) : (
                <>
                  <Typography
                    color={theme.sk.color.text.tertiary}
                    variant="paragraph-sm"
                  >
                    {i18n.t(
                      'sales_portal.order_entry.table.price_override_active',
                    )}
                  </Typography>
                  <Tooltip
                    placement="bottom left"
                    tabIndex={-1}
                    trigger={
                      <View style={styles.salesRepOverrideTooltipIcon}>
                        <InfoIcon
                          height={theme.sk.spacing.spaceSMedium}
                          stroke={theme.sk.color.icon.primary}
                          width={theme.sk.spacing.spaceSMedium}
                        />
                      </View>
                    }
                  >
                    <Typography
                      color={theme.sk.color.text.primary}
                      variant="paragraph-sm"
                    >
                      {i18n.t('sales_portal.order_entry.table.updated_by', {
                        name: salesRepUpdatedByName,
                      })}{' '}
                    </Typography>
                  </Tooltip>
                </>
              )}
            </View>
          ) : null}
        </TouchableOpacity>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    backgroundColor: theme.colors.tileColor,
    borderRadius: 8,
    paddingVertical: 4,
  },
  contentListView: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  divider: {
    borderRightColor: theme.colors.divider,
    borderRightWidth: 1,
  },
  editPriceButton: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 16,
  },
  editPriceMobileButton: {
    borderTopColor: theme.colors.divider,
    borderTopWidth: 1,
  },
  editPriceTextStyle: {
    ...TYPOGRAPHY.pSemiBold,
    fontSize: theme.fontSizes.small,
  },
  inputButtons: {
    marginLeft: 12,
    top: -4, // This is an arbitrary value that looks good
    zIndex: 1,
  },
  inputRightElementText: {
    ...FONTS.primarySemiBold,
    color: theme.colors.inactiveText,
  },
  row: {
    flexDirection: 'row',
  },
  salesCostContainer: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  salesCostContent: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
  salesCostIcon: {
    marginLeft: 2,
  },
  salesRepOverrideTooltip: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    justifyContent: 'center',
  },
  salesRepOverrideTooltipContainer: {
    alignItems: 'center',
    borderTopColor: theme.colors.divider,
    borderTopWidth: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    paddingBottom: theme.sk.spacing.spaceXsmall,
    paddingLeft: theme.sk.spacing.spaceSMedium,
    paddingTop: theme.sk.spacing.spaceSmall,
  },
  salesRepOverrideTooltipIcon: {
    marginLeft: theme.sk.spacing.spaceXsmall,
  },
}));
